import { useState, useMemo, useCallback } from 'react';

type SortDirection = 'asc' | 'desc' | false;

interface SortState {
  key: string;
  direction: SortDirection;
}

type SortFn<T> = (a: T, b: T, direction: 'asc' | 'desc') => number;

export function useTableSorting<T>(
  data: T[],
  defaultSort?: { key: string; direction: 'asc' | 'desc' },
  sortFns?: Record<string, SortFn<T>>
) {
  const [sorting, setSorting] = useState<SortState>(
    defaultSort ? { key: defaultSort.key, direction: defaultSort.direction } : { key: '', direction: false }
  );

  const handleSort = useCallback((key: string) => {
    setSorting((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      if (prev.direction === 'desc') return { key: '', direction: false };
      return { key, direction: 'asc' };
    });
  }, []);

  const getSortDirection = useCallback(
    (key: string): SortDirection => {
      return sorting.key === key ? sorting.direction : false;
    },
    [sorting]
  );

  const sortedData = useMemo(() => {
    if (!sorting.direction || !sorting.key) return data;

    const customFn = sortFns?.[sorting.key];
    const direction = sorting.direction as 'asc' | 'desc';

    return [...data].sort((a, b) => {
      if (customFn) return customFn(a, b, direction);

      const aVal = (a as Record<string, unknown>)[sorting.key];
      const bVal = (b as Record<string, unknown>)[sorting.key];

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return direction === 'asc' ? cmp : -cmp;
    });
  }, [data, sorting, sortFns]);

  return { sortedData, sorting, handleSort, getSortDirection };
}
